import React, { useCallback, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import RecordApproval from "@/components/RecordApproval";
import { FileSpreadsheet, MessageCircle, Phone, Pencil, Plus, Printer, RefreshCw, Search, Trash2, Upload } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth } from "@/contexts/AuthContext";
import { gradesForSchool, normalizeGrade } from "@/lib/sectionConfigs";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import api, { formatErr } from "@/lib/api";
import { toast } from "sonner";
import SchoolDocumentHeader from "@/components/SchoolDocumentHeader";
import { whatsappHref, whatsappPhone } from "@/lib/whatsapp";

const emptyStudent = {
  reference_no: "",
  student_name: "",
  student_id: "",
  contact_number: "",
  father_phone: "",
  mother_phone: "",
  grade: "",
  gender: "",
};

function ParentPhone({ label, phone, studentName, onContact, busy }) {
  if (!phone) return null;
  const href = whatsappHref(phone, `السلام عليكم، ولي أمر الطالب/ة ${studentName}، نود التواصل معكم من المدرسة.`);
  const callHref = whatsappPhone(phone) ? `tel:+${whatsappPhone(phone)}` : "";
  return (
    <div className="flex items-center gap-2 whitespace-nowrap">
      <span>{label}: {phone}</span>
      {href && (
        <a
          href={href}
          aria-disabled={busy}
          onClick={onContact ? (event) => { event.preventDefault(); if (!busy) onContact(label, phone, "واتساب", href); } : undefined}
          className="inline-flex h-7 items-center justify-center gap-1 rounded-full bg-[#25D366]/10 px-2 text-xs font-medium text-[#128C4A] hover:bg-[#25D366]/20"
          aria-label={`إرسال واتساب إلى ${label} للطالب ${studentName}`}
          title={`إرسال واتساب إلى ${label}`}
        >
          <MessageCircle className="h-4 w-4" aria-hidden="true" />
          <span>واتساب</span>
        </a>
      )}
      {callHref && (
        <a href={callHref} aria-disabled={busy}
          onClick={onContact ? (event) => { event.preventDefault(); if (!busy) onContact(label, phone, "هاتف", callHref); } : undefined}
          className="inline-flex items-center gap-1 rounded-full bg-[#3E6A56]/10 px-2 py-1 text-xs text-[#3E6A56]"
          aria-label={`اتصال إلى ${label} للطالب ${studentName}`}>
          <Phone className="h-4 w-4" aria-hidden="true" />اتصال
        </a>
      )}
    </div>
  );
}

export default function StudentRecords({ counselorId = null, readOnly = false, schoolKey = null, schoolName = "", counselorGender = "" }) {
  const { user } = useAuth();
  readOnly = readOnly || user?.role !== "counselor" || Boolean(counselorId && counselorId !== user?.id);
  const activeSchoolKey = schoolKey || user?.school_key;
  const activeSchoolName = schoolName || user?.school_name || "";
  const activeCounselorGender = counselorGender || (!counselorId && user?.role === "counselor" ? user?.gender : "");
  const grades = gradesForSchool(activeSchoolKey, activeCounselorGender);
  const [records, setRecords] = useState([]);
  const [search, setSearch] = useState("");
  const [selectedGrade, setSelectedGrade] = useState("all");
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [importing, setImporting] = useState(false);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [studentForm, setStudentForm] = useState(emptyStudent);
  const [saving, setSaving] = useState(false);
  const [recordToDelete, setRecordToDelete] = useState(null);
  const [deleteAllOpen, setDeleteAllOpen] = useState(false);
  const [deletingAll, setDeletingAll] = useState(false);
  const [contactBusy, setContactBusy] = useState(false);
  const [colleagues, setColleagues] = useState([]);
  const [callerId, setCallerId] = useState("");
  const [contactDraft, setContactDraft] = useState(() => {
    const recordId = new URLSearchParams(window.location.search).get("contact_record");
    return !readOnly && recordId ? { editHref: `/counselor/parents?edit=${encodeURIComponent(recordId)}` } : null;
  });
  const contactLock = React.useRef(false);

  useEffect(() => {
    if (readOnly || user?.role !== "counselor") return undefined;
    let cancelled = false;
    api.get("/counselors/colleagues").then(({ data }) => {
      if (cancelled) return;
      const list = Array.isArray(data) ? data : [];
      setColleagues(list);
      let saved = "";
      try { saved = localStorage.getItem(`caller_${user.id}`) || ""; } catch { saved = ""; }
      setCallerId(list.some((c) => c.id === saved) ? saved : user.id);
    }).catch(() => { if (!cancelled) setCallerId(user?.id || ""); });
    return () => { cancelled = true; };
  }, [readOnly, user?.id, user?.role]);

  const changeCaller = (value) => {
    setCallerId(value);
    try { localStorage.setItem(`caller_${user.id}`, value); } catch { /* التخزين المحلي اختياري */ }
  };
  const callerName = (colleagues.find((c) => c.id === callerId) || {}).name || user?.name || "";

  const startContact = async (student, parent, phone, method, href) => {
    if (readOnly || contactLock.current) return;
    contactLock.current = true;
    setContactBusy(true);
    setContactDraft(null);
    try {
      const now = new Date();
      const date = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
      const { data } = await api.post("/records", {
        type: "parent_contact",
        data: {
          student_id: student.student_id, student: student.student_name,
          grade: student.grade, parent, phone, method, date,
          caller_id: callerId || user?.id, caller: callerName,
          contact_status: "بدء التواصل", reason: [], recommendations: [],
        },
      });
      if (!data?.id) throw new Error("تعذر تحديد سجل بدء التواصل المحفوظ؛ راجع سجل أولياء الأمور قبل المحاولة مجددًا");
      const editHref = `/counselor/parents?edit=${encodeURIComponent(data.id)}`;
      setContactDraft({ href, editHref });
      toast.success("تم تسجيل بدء التواصل؛ أكمل السبب والتوصيات في سجل أولياء الأمور");
      const returnUrl = new URL(window.location.href);
      returnUrl.searchParams.set("contact_record", data.id);
      window.history.replaceState(window.history.state, "", returnUrl);
      window.location.assign(href);
    } catch (error) {
      toast.error(formatErr(error));
    } finally {
      contactLock.current = false;
      setContactBusy(false);
    }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { type: "student_record" };
      if (counselorId) params.counselor_id = counselorId;
      const { data } = await api.get("/records", { params });
      setRecords(data);
    } catch (error) {
      toast.error(formatErr(error));
    } finally {
      setLoading(false);
    }
  }, [counselorId]);

  useEffect(() => { load(); }, [load]);

  const importFile = async () => {
    if (!file) return;
    const body = new FormData();
    body.append("file", file);
    setImporting(true);
    try {
      const { data } = await api.post("/students/import", body);
      toast.success(`تم استيراد ${data.created} طالب وتحديث ${data.updated} سجل`);
      setFile(null);
      await load();
    } catch (error) {
      toast.error(formatErr(error));
    } finally {
      setImporting(false);
    }
  };

  const openEditor = (record = null) => {
    setEditingRecord(record);
    setStudentForm(record ? { ...emptyStudent, ...record.data, grade: normalizeGrade(record.data.grade) } : { ...emptyStudent });
    setEditorOpen(true);
  };

  const saveStudent = async (event) => {
    event.preventDefault();
    setSaving(true);
    try {
      const payload = { data: studentForm };
      if (editingRecord) {
        await api.put(`/students/${editingRecord.id}`, payload);
        toast.success("تم تحديث سجل الطالب");
      } else {
        await api.post("/students", payload);
        toast.success("تمت إضافة الطالب");
      }
      setEditorOpen(false);
      await load();
    } catch (error) {
      toast.error(formatErr(error));
    } finally {
      setSaving(false);
    }
  };

  const deleteStudent = async () => {
    if (!recordToDelete) return;
    try {
      const { data } = await api.delete(`/students/${recordToDelete.id}`);
      const updatedSessions = data.updated_group_sessions || 0;
      toast.success(`تم حذف سجل الطالب و${data.deleted_linked_records || 0} سجل مرتبط${updatedSessions ? ` وتحديث ${updatedSessions} جلسة جماعية` : ""}`);
      setRecordToDelete(null);
      await load();
    } catch (error) {
      toast.error(formatErr(error));
    }
  };

  const deleteAllStudents = async () => {
    setDeletingAll(true);
    try {
      const { data } = await api.delete("/students");
      toast.success(`تم حذف ${data.deleted_students} طالب و${data.deleted_linked_records} سجل مرتبط${data.updated_group_sessions ? ` وتحديث ${data.updated_group_sessions} جلسة جماعية` : ""}`);
      setDeleteAllOpen(false);
      await load();
    } catch (error) {
      toast.error(formatErr(error));
    } finally {
      setDeletingAll(false);
    }
  };

  const filtered = records.filter(({ data }) => {
    if (selectedGrade !== "all" && normalizeGrade(data.grade) !== selectedGrade) return false;
    return `${data.student_name} ${data.student_id} ${data.grade} ${data.father_phone} ${data.mother_phone}`
      .toLowerCase()
      .includes(search.trim().toLowerCase());
  });
  const gradeCounts = records.reduce((counts, { data }) => {
    const grade = normalizeGrade(data.grade) || "غير محدد";
    counts.set(grade, (counts.get(grade) || 0) + 1);
    return counts;
  }, new Map());
  const orderedGradeCounts = [
    ...grades.filter((grade) => gradeCounts.has(grade)).map((grade) => [grade, gradeCounts.get(grade)]),
    ...[...gradeCounts.entries()].filter(([grade]) => !grades.includes(grade)),
  ];

  const printRecords = () => {
    document.body.classList.add("printing-student-records");
    window.addEventListener("afterprint", () => document.body.classList.remove("printing-student-records"), { once: true });
    window.print();
  };

  return (
    <div className="space-y-6">
      <RecordApproval section="student_record" counselorId={counselorId} contentCount={records.length} />
      <div>
        <div className="mb-2 text-sm font-medium text-[#3E6A56]">بيانات الطلاب</div>
        <h1 className="text-3xl font-bold text-[#1A201C]" style={{ fontFamily: "IBM Plex Sans Arabic" }}>سجلات الطلاب</h1>
        <p className="mt-2 text-[#4A554E]">استورد ملف Excel الخاص بطلابك، وتواصل مع أولياء الأمور مباشرة عبر واتساب.</p>
      </div>

      {!readOnly && (
        <div className="space-y-4 rounded-2xl border border-[#e6e2d8] bg-white p-5">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
            <div className="min-w-0 flex-1">
              <label htmlFor="student-records-file" className="mb-2 block text-sm font-semibold text-[#1A201C]">ملف الطلاب بصيغة Excel (.xlsx)</label>
              <Input
                id="student-records-file"
                type="file"
                accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                onChange={(event) => setFile(event.target.files?.[0] || null)}
                className="rounded-xl bg-white"
              />
            </div>
            <Button onClick={importFile} disabled={!file || importing} className="rounded-xl bg-[#3E6A56] hover:bg-[#2D4F40]">
              <Upload className="me-2 h-4 w-4" />
              {importing ? "جاري الاستيراد..." : "استيراد سجلات الطلاب"}
            </Button>
          </div>
          <Button type="button" variant="outline" onClick={() => openEditor()} className="rounded-xl border-[#3E6A56]/30 text-[#3E6A56]">
            <Plus className="me-2 h-4 w-4" />إضافة طالب يدويًا
          </Button>
        </div>
      )}

      <section className="rounded-2xl border border-[#e6e2d8] bg-white p-4 sm:p-5" aria-label="إحصائية أعداد الطلاب">
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
          <h2 className="font-bold text-[#1A201C]">عدد الطلاب حسب الصف</h2>
          <span className="rounded-full bg-[#3E6A56]/10 px-3 py-1 text-sm font-bold text-[#3E6A56]" data-testid="student-total-count">
            العدد الكلي: {records.length}
          </span>
        </div>
        {loading ? (
          <p className="text-sm text-[#4A554E]">جاري تحميل الأعداد...</p>
        ) : orderedGradeCounts.length ? (
          <div className="flex flex-wrap gap-2">
            {orderedGradeCounts.map(([grade, count]) => (
              <span key={grade} className="rounded-xl border border-[#e6e2d8] bg-[#F9F8F6] px-3 py-2 text-sm text-[#303832]" data-testid="student-grade-count">
                {grade}: <strong>{count}</strong>
              </span>
            ))}
          </div>
        ) : (
          <p className="text-sm text-[#4A554E]">لا توجد سجلات طلاب.</p>
        )}
      </section>

      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div className="flex w-full flex-col gap-3 sm:flex-row sm:items-end">
          <div className="relative w-full sm:max-w-sm">
            <Search className="absolute start-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#4A554E]" />
            <Input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="بحث باسم الطالب أو رقمه..." className="rounded-xl bg-white ps-10" />
          </div>
          <div className="w-full sm:max-w-[200px]">
            <Label htmlFor="student-records-grade" className="mb-1.5 block text-sm">عرض الصف</Label>
            <Select value={selectedGrade} onValueChange={setSelectedGrade}>
              <SelectTrigger id="student-records-grade" className="rounded-xl bg-white"><SelectValue placeholder="جميع الصفوف" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">جميع الصفوف</SelectItem>
                {grades.map((grade) => <SelectItem key={grade} value={grade}>{grade}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button type="button" variant="outline" onClick={printRecords} disabled={loading || filtered.length === 0} className="rounded-xl">
            <Printer className="me-2 h-4 w-4" /> طباعة السجلات
          </Button>
          {!readOnly && (
            <Button variant="outline" onClick={() => setDeleteAllOpen(true)} disabled={loading || records.length === 0} className="rounded-xl border-[#B84B4B]/40 text-[#B84B4B] hover:bg-[#B84B4B]/5">
              <Trash2 className="me-2 h-4 w-4" /> حذف جميع السجلات
            </Button>
          )}
          <Button variant="outline" onClick={load} disabled={loading} className="rounded-xl">
            <RefreshCw className="me-2 h-4 w-4" /> تحديث
          </Button>
        </div>
      </div>

      {createPortal(<article className="student-records-print" dir="rtl">
        <SchoolDocumentHeader schoolKey={activeSchoolKey} gender={activeCounselorGender} schoolName={activeSchoolName} />
        <h1>سجلات الطلاب</h1>
        <p>عدد السجلات: {filtered.length}</p>
        <table>
          <thead>
            <tr>
              {["م", "اسم الطالب", "رقم الطالب", "الصف", "رقم الأب", "رقم الأم"].map((heading) => (
                <th key={heading}>{heading}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filtered.map(({ id, data }, index) => (
              <tr key={id}>
                <td>{index + 1}</td>
                <td>{data.student_name || "—"}</td>
                <td dir="ltr">{data.student_id || "—"}</td>
                <td>{data.grade || "—"}</td>
                <td dir="ltr">{data.father_phone || "—"}</td>
                <td dir="ltr">{data.mother_phone || "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </article>, document.body)}

      {!readOnly && colleagues.length > 1 && (
        <div className="flex flex-wrap items-center gap-2 rounded-xl border border-[#e6e2d8] bg-white px-4 py-3 text-sm" data-testid="caller-picker">
          <Label htmlFor="parent-caller" className="font-semibold">المتصل بولي الأمر:</Label>
          <select
            id="parent-caller"
            value={callerId}
            onChange={(event) => changeCaller(event.target.value)}
            className="rounded-lg border border-[#e6e2d8] bg-white px-3 py-1.5"
          >
            {colleagues.map((c) => <option key={c.id} value={c.id}>{c.name}{c.id === user?.id ? " (أنا)" : ""}</option>)}
          </select>
          <span className="text-xs text-[#4A554E]">يُسجَّل اسمه في سجل أولياء الأمور عند الضغط على اتصال أو واتساب.</span>
        </div>
      )}
      {!readOnly && <p className="text-xs text-[#4A554E]">الضغط على اتصال أو واتساب يسجل بدء التواصل، ولا يؤكد إتمام المكالمة أو إرسال الرسالة. أكمل السبب والتوصيات وحالة التواصل في سجل أولياء الأمور.</p>}
      {contactDraft && <div role="status" className="rounded-xl border border-[#3E6A56]/30 bg-white p-4 space-y-2">
        <p>حُفظ سجل بدء التواصل. يمكنك فتح وسيلة التواصل ثم استكمال السجل دون إنشاء سجل جديد.</p>
        {contactDraft.href && <a href={contactDraft.href} className="me-4 text-[#3E6A56] underline">فتح التواصل</a>}
        <a href={contactDraft.editHref} className="text-[#3E6A56] underline">استكمال سجل أولياء الأمور</a>
      </div>}
      <div className="overflow-hidden rounded-2xl border border-[#e6e2d8] bg-white">
        {loading ? (
          <div className="p-10 text-center text-[#4A554E]">جاري تحميل سجلات الطلاب...</div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center gap-2 p-10 text-center text-[#4A554E]">
            <FileSpreadsheet className="h-8 w-8 text-[#3E6A56]" />
            <span>{records.length ? "لا توجد نتائج مطابقة." : "لا توجد سجلات طلاب مستوردة لهذا الحساب بعد."}</span>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[850px] text-sm">
              <thead className="bg-[#F9F8F6] text-[#1A201C]">
                <tr>
                  {["م", "اسم الطالب", "رقم الطالب", "الصف", "أرقام التواصل", ...(!readOnly ? ["الإجراءات"] : [])].map((heading) => (
                    <th key={heading} className="px-4 py-3 text-start font-semibold">{heading}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map((record, index) => {
                  const { id, data } = record;
                  return (
                  <tr key={id} className="border-t border-[#f0ebe0]">
                    <td className="px-4 py-3 text-[#4A554E]">{index + 1}</td>
                    <td className="px-4 py-3 font-medium text-[#1A201C]">{data.student_name || "—"}</td>
                    <td className="px-4 py-3" dir="ltr">{data.student_id || "—"}</td>
                    <td className="px-4 py-3">{data.grade || "—"}</td>
                    <td className="space-y-2 px-4 py-3">
                      <ParentPhone label="الأب" phone={data.father_phone} studentName={data.student_name} busy={contactBusy} onContact={!readOnly ? (...args) => startContact(data, ...args) : undefined} />
                      <ParentPhone label="الأم" phone={data.mother_phone} studentName={data.student_name} busy={contactBusy} onContact={!readOnly ? (...args) => startContact(data, ...args) : undefined} />
                      {!data.father_phone && !data.mother_phone && "—"}
                    </td>
                    {!readOnly && (
                      <td className="px-4 py-3">
                        <div className="flex gap-1">
                          <Button type="button" size="sm" variant="ghost" onClick={() => openEditor({ id, data })} title="تعديل بيانات الطالب" aria-label={`تعديل بيانات ${data.student_name}`}>
                            <Pencil className="h-4 w-4 text-[#3E6A56]" />
                          </Button>
                          <Button type="button" size="sm" variant="ghost" onClick={() => setRecordToDelete({ id, data })} title="حذف سجل الطالب" aria-label={`حذف سجل ${data.student_name}`} className="text-[#B84B4B]">
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </td>
                    )}
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
      <p className="text-xs text-[#4A554E]">عدد السجلات: {records.length}</p>

      {!readOnly && (
        <>
          <Dialog open={editorOpen} onOpenChange={setEditorOpen}>
            <DialogContent dir="rtl" className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
              <DialogHeader>
                <DialogTitle>{editingRecord ? "تعديل سجل الطالب" : "إضافة طالب"}</DialogTitle>
                <DialogDescription>أدخل بيانات الطالب وأرقام التواصل، ثم احفظ التغييرات.</DialogDescription>
              </DialogHeader>
              <form onSubmit={saveStudent} className="grid grid-cols-1 gap-4 pt-2 sm:grid-cols-2">
                {[
                  ["student_name", "اسم الطالب", true],
                  ["student_id", "رقم الطالب", true],
                  ["grade", "الصف", false],
                  ["father_phone", "رقم الأب", false],
                  ["mother_phone", "رقم الأم", false],
                ].map(([field, label, required]) => (
                  <div key={field}>
                    <Label htmlFor={`student-${field}`} className="mb-1.5 block">{label}</Label>
                    {field === "grade" ? (
                      <Select value={studentForm.grade} onValueChange={(grade) => setStudentForm((current) => ({ ...current, grade }))}>
                        <SelectTrigger id="student-grade" className="rounded-xl bg-white"><SelectValue placeholder="اختر الصف" /></SelectTrigger>
                        <SelectContent>
                          {grades.map((grade) => <SelectItem key={grade} value={grade}>{grade}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    ) : <Input
                      id={`student-${field}`}
                      value={studentForm[field] || ""}
                      required={required}
                      onChange={(event) => setStudentForm((current) => ({ ...current, [field]: event.target.value }))}
                      className="rounded-xl bg-white"
                    />}
                  </div>
                ))}
                <DialogFooter className="gap-2 sm:col-span-2">
                  <Button type="button" variant="outline" onClick={() => setEditorOpen(false)} className="rounded-xl">إلغاء</Button>
                  <Button type="submit" disabled={saving} className="rounded-xl bg-[#3E6A56] hover:bg-[#2D4F40]">
                    {saving ? "جاري الحفظ..." : editingRecord ? "حفظ التعديلات" : "إضافة الطالب"}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
          <AlertDialog open={Boolean(recordToDelete)} onOpenChange={(open) => { if (!open) setRecordToDelete(null); }}>
            <AlertDialogContent dir="rtl">
              <AlertDialogHeader>
                <AlertDialogTitle>تأكيد حذف سجل الطالب بالكامل</AlertDialogTitle>
                <AlertDialogDescription>
                  سيتم حذف سجل الطالب {recordToDelete?.data.student_name} وسجلاته المرتبطة به، وإزالة اسمه من الجلسات الجماعية مع إبقاء بقية الطلاب. لا يمكن التراجع عن هذا الحذف.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>إلغاء</AlertDialogCancel>
                <AlertDialogAction onClick={deleteStudent} className="bg-[#B84B4B] text-white hover:bg-[#963A3A]">حذف الطالب وجميع سجلاته</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
          <AlertDialog open={deleteAllOpen} onOpenChange={(open) => { if (!deletingAll) setDeleteAllOpen(open); }}>
            <AlertDialogContent dir="rtl">
              <AlertDialogHeader>
                <AlertDialogTitle>تأكيد حذف جميع سجلات الطلاب</AlertDialogTitle>
                <AlertDialogDescription>
                  سيتم حذف جميع الطلاب ({records.length}) وجميع سجلاتهم الإرشادية المرتبطة. ستُزال مشاركاتهم من الجلسات الجماعية مع الإبقاء على أي طلاب آخرين غير مشمولين. هذا الإجراء نهائي ولا يمكن التراجع عنه.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel disabled={deletingAll}>إلغاء</AlertDialogCancel>
                <AlertDialogAction onClick={deleteAllStudents} disabled={deletingAll} className="bg-[#B84B4B] text-white hover:bg-[#963A3A]">
                  {deletingAll ? "جارٍ الحذف..." : "حذف جميع الطلاب وسجلاتهم"}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </>
      )}
    </div>
  );
}
